import { AfterViewInit, ChangeDetectorRef, Component, ContentChild, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation, ViewRef } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';

import { BooleanConverter } from '../../../inputConverter';
import { BaseComponent } from '../../base.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-select-ng',
    templateUrl: './select.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './select.component.scss']
})
export class SelectComponent extends BaseComponent implements AfterViewInit, OnInit {
    private _canAddCustom: boolean = false;
    private _data: any[] = null;
    private _hasVirtualScroll: boolean = false;
    private _isClearable: boolean = true;
    private _isDisabled: boolean = false;
    private _isLoading: boolean = false;
    private _isMultiSelect: boolean = false;
    private _isSearchable: boolean = false;
    private _model: any;

    loadedData: any[] = null;
    numberOfItemsFromEndBeforeLoadMore: number = 5;
    searchTerm: string = null;

    @ContentChild(TemplateRef) optionTemplate: TemplateRef<any> = null;

    @Input() controlLabel: string = 'Value';
    @Input() label: string = '';
    @Input() pageSize: number = 1000;
    @Input() placeholderText: string = '';
    @Input() value: string = '';

    @Input()
    set canAddCustom(value) {
        this._canAddCustom = BooleanConverter(value);
    }
    get canAddCustom() {
        return this._canAddCustom;
    }

    @Input()
    set data(value: any[]) {
        this._data = typeof value === 'string' ? JSON.parse(value) : value;

        if (!this._data) {
            this.loadedData = null;
        } else if (this.hasVirtualScroll) {
            this.loadedData = [];
            this.loadMoreData();
        } else {
            this.loadedData = [].concat(this._data);
        }

        this.showSelectedValue(false);
    }
    get data(): any[] {
        return this._data;
    }

    @Input()
    set hasVirtualScroll(value) {
        this._hasVirtualScroll = BooleanConverter(value);
    }
    get hasVirtualScroll() {
        return this._hasVirtualScroll;
    }

    @Input()
    set isClearable(value) {
        this._isClearable = BooleanConverter(value);
    }
    get isClearable() {
        return this._isClearable;
    }

    @Input()
    set isDisabled(value) {
        this._isDisabled = BooleanConverter(value);
        this.toggleDisabled();
    }
    get isDisabled() {
        return this._isDisabled;
    }

    @Input()
    set isLoading(value) {
        this._isLoading = BooleanConverter(value);
    }
    get isLoading() {
        return this._isLoading;
    }

    @Input()
    set isMultiSelect(value) {
        this._isMultiSelect = BooleanConverter(value);
    }
    get isMultiSelect() {
        return this._isMultiSelect;
    }

    @Input()
    set isSearchable(value) {
        this._isSearchable = BooleanConverter(value);
    }
    get isSearchable() {
        return this._isSearchable;
    }

    @Input()
    set model(value) {
        if (this._model !== value) {
            this._model = value;
            this.showSelectedValue(false);
        }
    }
    get model() {
        return this._model;
    }

    @ViewChild('select') private readonly selectRef: ElementRef<NgSelectComponent>;

    get searchBox() {
        return (<any>this.selectRef).element.querySelector('input');
    }

    @Output() readonly addEvent = new EventEmitter<any>();
    @Output() readonly blurEvent = new EventEmitter<any>();
    @Output() readonly clearEvent = new EventEmitter<any>();
    @Output() readonly closeEvent = new EventEmitter<any>();
    @Output() readonly focusEvent = new EventEmitter<any>();
    @Output() readonly modelChange = new EventEmitter<any>();
    @Output() readonly openEvent = new EventEmitter<any>();
    @Output() readonly removeEvent = new EventEmitter<any>();
    @Output() readonly searchEvent = new EventEmitter<any>();

    constructor(_vcr: ViewContainerRef, private readonly cdr: ChangeDetectorRef) { super(_vcr); }

    ngOnInit() {
        try {
            this.registerEvent('addEvent', this.addEvent);
            this.registerEvent('blurEvent', this.blurEvent);
            this.registerEvent('clearEvent', this.clearEvent);
            this.registerEvent('closeEvent', this.closeEvent);
            this.registerEvent('focusEvent', this.focusEvent);
            this.registerEvent('modelChange', this.modelChange);
            this.registerEvent('openEvent', this.openEvent);
            this.registerEvent('removeEvent', this.removeEvent);
            this.registerEvent('searchEvent', this.searchEvent);
        } catch (error) {
            this.readyProblem(error);
        } finally {
            this.isReady();
        }
    }

    ngAfterViewInit() {
        this.toggleDisabled();

        // When items are added to the dropdown, the search box is cleared. Capture the search as the user types so it can be put back.

        (<any>this.selectRef).element.querySelector('input').addEventListener('input', e => {
            this.searchTerm = e.target.value;
        });
    }

    formatCustom = (label) => {
        if (this.canAddCustom) {
            const customItem = {};
            customItem[this.label] = label;
            customItem[this.value] = label;
            return customItem;
        } else {
            return false;
        }
    }

    private loadMoreData() {
        const additionalData = this._data.slice(this.loadedData.length, this.pageSize + Number(this.loadedData.length));
        this.toggleLoading(true);
        this.loadedData = this.loadedData.concat(additionalData);

        if (this.searchTerm) {
            this.searchBox.readOnly = true;

            setTimeout(() => {
                this.searchBox.value = this.searchTerm;
                (<any>this.selectRef).filter(this.searchTerm);
                this.searchBox.readOnly = false;
            }, 0);
        }

        if (this.loadedData.length < this._data.length) {
            setTimeout(() => this.loadMoreData(), 250);
        } else {
            this.toggleLoading(false);
        }
    }

    onAdd(event) {
        this.addEvent.emit(event);
    }

    onBlur(event) {
        this.blurEvent.emit(event);
    }

    onChange(_event) {
        this.searchTerm = null;
        this.showSelectedValue(true);
    }

    onClear() {
        this.setDropdownPanelStyle();
        this.clearEvent.emit();
    }

    onClose() {
        if (this.searchTerm !== null && this._model !== this.searchTerm) {
            this.searchTerm = null;
            this.showSelectedValue(false);
        }

        const selectElement = (this.selectRef as any).element;
        const dropdownPanel = selectElement.querySelectorAll('.ng-dropdown-panel')[0];
        dropdownPanel.style.opacity = '0';

        this.closeEvent.emit();
    }

    onFocus(event) {
        this.focusEvent.emit(event);
    }

    onOpen() {
        this.setDropdownPanelStyle();
        this.openEvent.emit();
    }

    onRemove(event) {
        this.setDropdownPanelStyle();
        this.removeEvent.emit(event);
    }

    onSearch(event) {
        this.searchTerm = event.term;
        this.searchEvent.emit(event);
    }

    private setDropdownPanelStyle() {
        // Dropdown with items needs fixed position and location set manually to ensure is not contained within popup and do not need to scroll popup to view items
        setTimeout(() => {
            const selectElement = (this.selectRef as any).element;
            const dropdownPanel = selectElement.querySelectorAll('.ng-dropdown-panel')[0];

            if (dropdownPanel) {
                const selectContainerBoundingBox = selectElement.querySelectorAll('.ng-select-container')[0].getBoundingClientRect();
                dropdownPanel.style.opacity = '1';
                dropdownPanel.style.left = `${selectContainerBoundingBox.left}px`;
                dropdownPanel.style.top = `${selectContainerBoundingBox.bottom}px`;
                dropdownPanel.style.width = `${selectContainerBoundingBox.width}px`;
            }
        }, 0);
    }

    private showSelectedValue(shouldTriggerEvent) {
        setTimeout(() => {
            const selectElement = (this.selectRef as any).element;

            if (this.isMultiSelect && this._model) {
                const valueLabels = selectElement.querySelectorAll('.ng-value-label');

                if (valueLabels && valueLabels.length > 0) {
                    this._model.forEach((selectedItem, index) => {
                        if (this.loadedData && !this.loadedData.find(data => data[this.value] === selectedItem)) {
                            const modelData = this.data.find(data => data[this.value] === selectedItem);

                            if (modelData) {
                                valueLabels[index].innerText = modelData[this.label];
                            } else {
                                valueLabels[index].innerText = selectedItem;
                            }
                        } else if (!this.loadedData) {
                            valueLabels[index].innerText = selectedItem;
                        }
                    });
                }
            } else if (!this.isMultiSelect) {
                const valueLabel = selectElement.querySelectorAll('.ng-value-label')[0];
                const input = selectElement.querySelectorAll('input')[0];

                if (this.loadedData && this._model && !this.loadedData.find(data => data[this.value] === this._model)) {
                    const modelData = this.data.find(data => data[this.value] === this._model);

                    if (modelData) {
                        valueLabel.innerText = modelData[this.label];
                        input.value = modelData[this.label];
                    } else {
                        valueLabel.innerText = this.model;
                        input.value = this.model;
                    }
                } else {
                    input.value = valueLabel ? valueLabel.innerText : '';
                }
            }

            if (this.cdr && !(this.cdr as ViewRef).destroyed) {
                this.cdr.detectChanges();
            }

            if (shouldTriggerEvent) {
                this.modelChange.emit(this.model);
            }
        }, 1);
    }

    private toggleDisabled() {
        const selectElement = (this.selectRef as any).element;
        const input = selectElement.querySelectorAll('input')[0];

        if (input) {
            if (this._isDisabled) {
                input.setAttribute('disabled', '');
            } else {
                input.removeAttribute('disabled');
            }
        }
    }

    private toggleLoading(isLoading: boolean) {
        this.isLoading = isLoading;
        this.cdr.detectChanges();
    }
}
